import { api } from "../lib";
import { bridgeService, type BridgeCommand } from "./bridgeService";
import {
  buildXmlContent,
  slideLabelPrinterService,
  type SlideLabelData,
} from "./slideLabelPrinter.service";
import { cassetteLabelPrinterService } from "./cassetteLabelPrinter.service";
import type { CassetteLabel } from "../components/accessioning/CassetteLabelPreview";
import { getApiErrorStatus } from "../utils/apiError";
import {
  isUsablePatientLabelName,
  MISSING_PATIENT_LABEL_NAME_ERROR,
} from "../utils/patientLabelName";

export function waitForSelectedPrint(
  command: BridgeCommand
): Promise<BridgeCommand> {
  return pollSelectedPrint(command, Date.now() + 65000);
}

async function pollSelectedPrint(
  command: BridgeCommand,
  deadline: number
): Promise<BridgeCommand> {
  if (["pending", "sent", "acknowledged"].includes(command.status)) {
    if (Date.now() >= deadline)
      throw new Error(
        "Print completion was not confirmed. Check the printer before printing again."
      );
    await new Promise((resolve) => setTimeout(resolve, 500));
    const current = (await bridgeService.getCommand(command.id)).data;
    return pollSelectedPrint(current, deadline);
  }
  if (
    command.status !== "completed" ||
    !command.result ||
    command.result.success === false
  )
    throw new Error(
      "The printer could not complete this label. It has not been marked printed."
    );
  return command;
}

export function selectedPrintError(error: unknown): string {
  const status = getApiErrorStatus(error);
  if (status === 403) return "You do not have permission to print this label.";
  if (status === 409)
    return "This case or slide cannot be printed now. Review its current state.";
  if (status === 401) return "Your session ended. Sign in again to continue.";
  return "The print was not confirmed. Check the printer before printing again.";
}

export async function printSelectedSlide(input: {
  caseId: string;
  slideId: string;
  source: "cutting" | "case";
  label: SlideLabelData;
}): Promise<BridgeCommand> {
  const deviceId = slideLabelPrinterService.selectedDeviceId;
  if (!deviceId) throw new Error("Select a slide printer before printing.");
  if (!isUsablePatientLabelName(input.label.patientName))
    throw new Error(MISSING_PATIENT_LABEL_NAME_ERROR);
  const command = await api.post<BridgeCommand>(
    `/workflows/${input.source}/${input.caseId}/slides/${input.slideId}/print`,
    {
      deviceId,
      content: buildXmlContent(input.label),
    }
  );
  return waitForSelectedPrint(command);
}

export async function printSelectedCassette(
  label: CassetteLabel
): Promise<BridgeCommand> {
  const deviceId = cassetteLabelPrinterService.selectedDeviceId;
  if (!deviceId) throw new Error("Select a cassette printer before printing.");
  const command = await bridgeService.createCommand({
    deviceId,
    commandType: "print_cassette_labels",
    payload: {
      labels: [
        {
          content: `H6;${label.caseNumber};${label.specimenLetter};${label.blockNumber};${label.patientName};${label.facilityName}`,
          labelType: "cassette",
          copies: 1,
        },
      ],
    },
    expiresInSeconds: 60,
  });
  return waitForSelectedPrint(command.data);
}
